import { create } from "zustand";
import { studyApi } from "@/lib/api/study";
import type {
  StudyParticipantStatus,
  StudyWave,
  SubmitStudyResponseRequest,
} from "@/lib/types/study";

const T1_DELAY_MS = 14 * 24 * 60 * 60 * 1000;
const T2_DELAY_MS = 28 * 24 * 60 * 60 * 1000;
const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface StudyStore {
  status: StudyParticipantStatus | null;
  hasFetched: boolean;

  fetchStatus: () => Promise<void>;
  consent: () => Promise<StudyParticipantStatus>;
  submitResponse: (input: SubmitStudyResponseRequest) => Promise<void>;
  withdraw: () => Promise<void>;
  deleteMyData: () => Promise<void>;
  reset: () => void;
}

let inFlight: Promise<void> | null = null;
let generation = 0;

export const useStudyStore = create<StudyStore>((set) => ({
  status: null,
  hasFetched: false,

  fetchStatus: async () => {
    if (inFlight) return inFlight;
    const requested = generation;
    inFlight = (async () => {
      try {
        const status = await studyApi.getStatus();
        if (requested === generation) set({ status, hasFetched: true });
      } catch {
        // A failed request tells us nothing about enrolment
      } finally {
        if (requested === generation) inFlight = null;
      }
    })();
    return inFlight;
  },

  consent: async () => {
    const status = await studyApi.consent();
    set({ status, hasFetched: true });
    return status;
  },

  submitResponse: async (input) => {
    const status = await studyApi.submitResponse(input);
    set({ status });
  },

  withdraw: async () => {
    const status = await studyApi.withdraw();
    set({ status });
  },

  deleteMyData: async () => {
    await studyApi.deleteMyData();
    set({ status: null });
  },

  reset: () => {
    generation += 1;
    inFlight = null;
    set({ status: null, hasFetched: false });
  },
}));

export function canAnswerWave(
  status: StudyParticipantStatus | null,
  wave: StudyWave,
  now: Date = new Date(),
): boolean {
  if (!status || status.withdrawnAt) return false;

  if (wave === "T0") return !status.t0CompletedAt;

  const priorDone = wave === "T1" ? status.t0CompletedAt : status.t1CompletedAt;
  const alreadyDone =
    wave === "T1" ? status.t1CompletedAt : status.t2CompletedAt;
  if (!priorDone || alreadyDone) return false;

  const delay = wave === "T1" ? T1_DELAY_MS : T2_DELAY_MS;
  const unlockAt = new Date(priorDone).getTime() + delay;
  const nowMs = now.getTime();
  return nowMs >= unlockAt && nowMs <= unlockAt + WINDOW_MS;
}

export function isWaveLastDay(
  status: StudyParticipantStatus | null,
  wave: StudyWave,
  now: Date = new Date(),
): boolean {
  if (wave === "T0" || !canAnswerWave(status, wave, now)) return false;

  const priorDone =
    wave === "T1" ? status!.t0CompletedAt : status!.t1CompletedAt;
  const delay = wave === "T1" ? T1_DELAY_MS : T2_DELAY_MS;
  const closesAt = new Date(priorDone!).getTime() + delay + WINDOW_MS;
  return now.getTime() >= closesAt - ONE_DAY_MS;
}

export function nextWave(
  status: StudyParticipantStatus | null,
): StudyWave | null {
  if (!status || status.withdrawnAt) return null;
  if (!status.t0CompletedAt) return "T0";
  if (!status.t1CompletedAt) return "T1";
  if (!status.t2CompletedAt) return "T2";
  return null;
}
